import AnimationFadeIn from "@components/AnimationFadeIn";
import BackgroundImageCst from "@components/BackgroundImageCst";
import ButtonCst from "@components/ButtonCst";
import { Box, Grid, Stack } from "@mui/material";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { InputCst, SelectCst } from "@components/InputCst";
import { ColumnInterface } from "@components/Table/types";
import ConditionGroupsInputs from "./components/ConditionGroupsInputs";
import { defaultValidation } from "@utils/utilsValidators";
import { translateHeaders } from "@utils/utilsTranslateHeaders";

interface GroupsDynamicFormCstInterface {
  row?: any;
  dataSet?: any;
  columns: ColumnInterface[];
  onSubmit: (inputForm: any) => void;
  backButton?: () => any;
}

export interface GroupDynamicRule {
  id: number;
  operator: string;
  condition_field: string;
  condition: string;
  value: string;
  position: number;
  creation_date: string;
  creation_user: string;
  last_update_date: string;
  last_update_user: string;
}

export interface GroupDynamicRuleSet {
  rules: GroupDynamicRule[];
  operator?: string;
}

const groupOperatorOptions = [
  { label: "AND", value: "AND" },
  { label: "AND NOT", value: "AND_NOT" },
  { label: "OR", value: "OR" },
  { label: "OR NOT", value: "OR_NOT" },
];

const GroupsDynamicFormCst: React.FC<GroupsDynamicFormCstInterface> = ({
  row,
  columns,
  onSubmit,
  backButton,
}) => {
  const { t, i18n } = useTranslation(["form"]);
  const navigate = useNavigate();
  const theme = useTheme();

  const [inputForm, setInputForm] = React.useState<any>({});
  const [errorInputForm, setErrorInputForm] = React.useState<any>({});

  const getInitialData = () => {
    let initialState: any = {};
    columns?.forEach(({ accessorKey }) => {
      if (accessorKey === "sets") {
        if (Array.isArray(row.sets) && row.sets && row.sets.length > 0) {
          initialState.sets = [
            ...(initialState.sets ?? []),
            ...row.sets.map((s: GroupDynamicRuleSet) => ({
              ...s,
              operator: !s.operator || s.operator === "" ? "AND" : s.operator,
            })),
          ];
        } else {
          initialState.sets = [
            {
              rules: row.rules,
              operator: "AND",
            },
          ];
        }
      } else {
        initialState[accessorKey] = row[accessorKey];
      }
    });
    return initialState;
  };

  const resetInputForm = () => {
    let initialState = { groups: [] };

    if (row) {
      const initialData = getInitialData();
      setInputForm(initialData);
    } else {
      setInputForm(initialState);
    }
  };

  useEffect(() => {
    if (
      row &&
      Object.keys(row).length > 0 &&
      columns?.length > 0 &&
      Object.keys(inputForm).length === 0
    ) {
      const initialState = getInitialData();
      setInputForm(initialState);
    }
  }, [row, columns]);

  const onConfirm = () => {
    const { canBeSubmit, showError } = defaultValidation(columns, inputForm);
    setErrorInputForm(showError);
    if (canBeSubmit) {
      onSubmit(inputForm);
    }
  };

  const getErrMsg = (accessorKey: string): string => {
    return errorInputForm[accessorKey]?.msg || errorInputForm[accessorKey];
  };

  const minLength = (accessorKey: string): number => {
    return errorInputForm[accessorKey]?.value;
  };

  const maxLength = (accessorKey: string): number => {
    return errorInputForm[accessorKey]?.value;
  };

  const translatedInputCondition = useCallback(
    (cols: any) => {
      return translateHeaders(cols, i18n.language);
    },
    [columns, i18n.language],
  );

  const changeSetOperator = (
    accessorKey: string,
    setIndex: number,
    condition: string,
  ) => {
    let updatedSets = inputForm[accessorKey]
      ? ([...inputForm[accessorKey]] as GroupDynamicRuleSet[])
      : [];
    updatedSets[setIndex].operator = condition;
    setInputForm((current: any) => ({
      ...current,
      [accessorKey]: updatedSets,
    }));
  };

  const removeSet = (accessorKey: string, setIndex: number) => {
    let updatedSets = inputForm[accessorKey]
      ? ([...inputForm[accessorKey]] as GroupDynamicRuleSet[])
      : [];
    updatedSets = updatedSets.filter((_, i) => i !== setIndex);
    setInputForm((current: any) => ({
      ...current,
      [accessorKey]: updatedSets,
    }));
  };

  const addSet = (accessorKey: string) => {
    let updatedSets = inputForm[accessorKey]
      ? ([...inputForm[accessorKey]] as GroupDynamicRuleSet[])
      : [];
    updatedSets = [
      ...updatedSets,
      {
        rules: [],
        operator: "AND",
      },
    ];

    setInputForm((current: any) => ({
      ...current,
      [accessorKey]: updatedSets,
    }));
  };

  const updateSet = (accessorKey: string, setIndex: number, value: any) => {
    let updatedSets = inputForm[accessorKey]
      ? ([...inputForm[accessorKey]] as GroupDynamicRuleSet[])
      : [];
    updatedSets[setIndex].rules = value;
    setInputForm((current: any) => ({
      ...current,
      [accessorKey]: updatedSets,
    }));
  };

  const getInputsList = (col: any) => {
    switch (col.type) {
      case "text": {
        return (
          <Grid key={col.accessorKey} item xs={12} sm={6}>
            <InputCst
              key={col.accessorKey}
              id={col.accessorKey}
              label={col.header}
              value={inputForm[col.accessorKey]}
              required={col.required}
              onChange={(e: any) => {
                setInputForm({
                  ...inputForm,
                  ...{ [col.accessorKey]: e.target.value },
                });
              }}
              maxValue={col?.rules?.max?.value}
              multiline={col.multiline}
              rows={col.rows}
              error={errorInputForm[col.accessorKey] ? true : false}
              errorMessage={
                t(`error.field.${getErrMsg(col.accessorKey)}`, {
                  min: minLength(col.accessorKey),
                  max: maxLength(col.accessorKey),
                }) || "Error"
              }
              disabled={col.disabled}
              textTooltip={col.tooltip}
              type={col.inpuType || "text"}
            />
          </Grid>
        );
      }
      case "groups-conditions": {
        const ruleSets = inputForm[col.accessorKey] as GroupDynamicRuleSet[];

        return (
          <Grid item xs={12}>
            <Box
              sx={{
                border: `1px solid ${theme.palette.text.tertiary}`,
                padding: "32px 16px",
                display: "flex",
                flexDirection: "column",
                borderRadius: "4px",
                position: "relative",
              }}
            >
              <Box
                sx={{
                  padding: "5px",
                  position: "absolute",
                  top: 0,
                  left: "10px",
                  backgroundColor: theme?.palette.background.paper,
                  transform: "translate(0, -50%)",
                  color: theme.palette.text.primary,
                }}
              >
                {t("ruleSet")}
              </Box>
              {ruleSets?.map((group: GroupDynamicRuleSet, index: number) => {
                const errorKey = `${col.accessorKey}_${index}`;

                return (
                  <Fragment key={`rule_${index}`}>
                    {group.operator && index > 0 && (
                      <Stack
                        direction={"row"}
                        alignItems={"center"}
                        width={"100%"}
                        gap={3}
                        my={3}
                      >
                        <hr style={{ width: "100%" }} />
                        <SelectCst
                          id={`group_${index}_condition`}
                          key={`group_${index}_condition`}
                          options={groupOperatorOptions}
                          label={"Operatore"}
                          value={group.operator}
                          required={true}
                          onChange={(e: any) =>
                            changeSetOperator(col.accessorKey, index, e)
                          }
                        />
                        <hr style={{ width: "100%" }} />
                      </Stack>
                    )}

                    <ConditionGroupsInputs
                      label={`${t("group")} #${index + 1}`}
                      index={index}
                      totalSets={ruleSets.length}
                      fields={translatedInputCondition(col.fields)}
                      rules={group.rules || []}
                      disabled={col.disabled}
                      onChange={(e: any) =>
                        updateSet(col.accessorKey, index, e)
                      }
                      onRemoveSet={() => removeSet(col.accessorKey, index)}
                      getErrMsg={getErrMsg}
                      minLength={minLength}
                      maxLength={maxLength}
                      errorInputForm={errorInputForm}
                      setErrorInputForm={setErrorInputForm}
                      error={!!errorInputForm[errorKey]}
                      errorMessage={
                        t(`error.field.${getErrMsg(col.accessorKey)}`, {
                          min: minLength(col.accessorKey),
                          max: maxLength(col.accessorKey),
                        }) || "Error"
                      }
                    />
                  </Fragment>
                );
              })}
              <Box
                sx={{
                  alignSelf: "start",
                  mt: ruleSets?.length > 0 ? 3 : 0,
                }}
              >
                <ButtonCst
                  id={"add-button1"}
                  variant={"contained"}
                  size={"medium"}
                  sx={{
                    minWidth: "150px",
                    [theme.breakpoints.down("lg")]: {
                      minWidth: "100px",
                    },
                    [theme.breakpoints.down("sm")]: {
                      minWidth: "100%",
                    },
                  }}
                  onClick={() => addSet(col.accessorKey)}
                >
                  {t("add-dynamic-group-set-btn")}
                </ButtonCst>
              </Box>
            </Box>
          </Grid>
        );
      }
    }
  };

  const getInputField = useMemo(() => {
    return columns?.map((col: any) => {
      return getInputsList(col);
    });
  }, [columns, inputForm, errorInputForm]);

  const areSetsValidated = useMemo(() => {
    const sets = inputForm?.sets as GroupDynamicRuleSet[];
    return (
      sets &&
      sets.length > 0 &&
      sets.every((s) => s.rules && s.rules.length > 0)
    );
  }, [inputForm]);

  return (
    <AnimationFadeIn>
      <Grid container>
        <Grid item xs={0} lg={4} xl={3}>
          <BackgroundImageCst
            image="section-internal-form.jpg"
            disableGradient
          />
        </Grid>
        <Grid item xs={12} lg={8} xl={9}>
          <Box
            sx={{
              backgroundColor: theme.palette.background.paper,
              padding: "32px",
              minHeight: "495px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Grid container spacing={3}>
              {getInputField}
            </Grid>
            <Grid container spacing={3}>
              <Grid
                item
                xs={12}
                sx={{
                  mt: "32px",
                  display: "flex",
                  alignItems: "center",
                  gap: "20px",
                  justifyContent: "space-between",
                  [theme.breakpoints.down("sm")]: {
                    flexDirection: "column",
                  },
                }}
              >
                <ButtonCst
                  id={"add-button1"}
                  variant={"outlined"}
                  size={"medium"}
                  sx={{
                    minWidth: "150px",
                    mr: "auto",
                    [theme.breakpoints.down("lg")]: {
                      minWidth: "100px",
                    },
                    [theme.breakpoints.down("sm")]: {
                      minWidth: "100%",
                    },
                  }}
                  onClick={() => {
                    if (backButton) {
                      backButton();
                    } else {
                      navigate(-1);
                    }
                  }}
                >
                  {t("backButton")}
                </ButtonCst>
                <ButtonCst
                  id={"add-button1"}
                  variant={"outlined"}
                  size={"medium"}
                  sx={{
                    minWidth: "150px",
                    [theme.breakpoints.down("lg")]: {
                      minWidth: "100px",
                    },
                    [theme.breakpoints.down("sm")]: {
                      minWidth: "100%",
                    },
                  }}
                  onClick={resetInputForm}
                >
                  {t("resetButton")}
                </ButtonCst>
                <ButtonCst
                  id={"add-button1"}
                  variant={"contained"}
                  size={"medium"}
                  disabled={!areSetsValidated}
                  sx={{
                    minWidth: "150px",
                    [theme.breakpoints.down("lg")]: {
                      minWidth: "100px",
                    },
                    [theme.breakpoints.down("sm")]: {
                      minWidth: "100%",
                    },
                  }}
                  onClick={() => onConfirm()}
                >
                  {t("saveButton")}
                </ButtonCst>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </AnimationFadeIn>
  );
};

export default GroupsDynamicFormCst;
